import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";

const API = environment.apiUrl;
const base = `${API}/financial-review`;

const get = async (endpoint) => {
  const res = await customFetch(`${base}${endpoint}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Financial Review request failed (${res.status})`);
  }
  return res.json();
};

const post = async (endpoint, body) => {
  const res = await customFetch(`${base}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const responseBody = await res.json().catch(() => ({}));
    throw new Error(responseBody.error || `Financial Review request failed (${res.status})`);
  }
  return res.json();
};

const appendList = (params, key, value) => {
  const list = Array.isArray(value) ? value : value != null && value !== "" ? [value] : [];
  list.forEach((v) => {
    if (v != null && v !== "") params.append(key, v);
  });
};

const buildParams = ({ visn, station_id, specialty, report_month, state, new_visn, start_month, end_month } = {}) => {
  const params = new URLSearchParams();
  appendList(params, "visn", visn);
  appendList(params, "station_id", station_id);
  appendList(params, "specialty", specialty);
  if (report_month) params.set("report_month", report_month);
  appendList(params, "state", state);
  appendList(params, "new_visn", new_visn);
  if (start_month) params.set("start_month", start_month);
  if (end_month) params.set("end_month", end_month);
  return params.toString();
};

/* ── Filters ── */
// Each dimension's option list is scoped server-side to whichever OTHER
// filters (and report month) are currently selected -- e.g. picking a
// RISE VISN narrows the VISN/Specialty/State/Site lists to what actually
// has data under it that month. Pass the current filters so the bar
// refetches as the user narrows down, the same way the report itself does.
export const fetchFinancialReviewFilters = (filters) => get(`/filters?${buildParams(filters)}`);

/* ── Financial Review tab ── */
export const fetchFinancialReviewReport = (filters) => get(`/financialreview?${buildParams(filters)}`);

/* ── By site (paginated, click-to-page) ──
   `offset`/`limit` ride alongside the same visn/station_id/specialty/state/
   report_month filters as everything else on this tab. */
export const fetchFinancialReviewSites = ({ offset = 0, limit = 10, ...filters } = {}) => {
  const params = new URLSearchParams(buildParams(filters));
  params.set("offset", offset);
  params.set("limit", limit);
  return get(`/financialreview/sites?${params.toString()}`);
};

/* ── By VISN / By specialty / By location (paginated, click-to-page) ──
   Same idea as fetchFinancialReviewSites, but for whichever of those three
   breakdowns `dimension` names ("visn" | "specialty" | "location"). */
export const fetchFinancialReviewBreakdown = ({ dimension, offset = 0, limit = 10, ...filters } = {}) => {
  const params = new URLSearchParams(buildParams(filters));
  params.set("dimension", dimension);
  params.set("offset", offset);
  params.set("limit", limit);
  return get(`/financialreview/breakdown?${params.toString()}`);
};

/* ── Analysis tab ── */
export const fetchAnalysis = (filters) => get(`/analysis?${buildParams(filters)}`);

/* ── Retention tab (beta) ── */
export const fetchRetention = (filters) => get(`/retention?${buildParams(filters)}`);

/* ── LTV tab (beta) — all-time, not report_month-scoped, but buildParams
   just won't set that param if filters.report_month is undefined. ── */
export const fetchLtv = (filters) => get(`/ltv?${buildParams(filters)}`);

/* ── Pylon ticket sync (beta) — pulls live from Pylon's API into the
   in-memory ticket store GET /retention appends to the feedback list. ── */
export const syncPylonTickets = (start, end) => post("/retention/sync-tickets", { start, end });
